import React from "react";
import { FlexCol, FlexRow } from "./Containers";
import { BCHeader4 } from "./BCHeaders";
import { BCTertiaryButton } from "./BCButtons";
import { Body2 } from "./BCHeaders";
import { Tag } from "./BCTag";

interface ItemsFilterProps<T> {
    items: T[];
    allTags: Tag[];
    getItemTags: (item: T) => Tag[] | undefined;
    onFilteredItemsChange: (filteredItems: T[]) => void;
    maxTagsToShow?: number;
}

export const ItemsFilter = <T,>({
    items,
    allTags,
    getItemTags,
    onFilteredItemsChange,
    maxTagsToShow = 6
}: ItemsFilterProps<T>) => {
    const [selectedTags, setSelectedTags] = React.useState<Tag[]>([]);
    const [showAllTags, setShowAllTags] = React.useState(false);
    
    const displayedTags = showAllTags ? allTags : allTags.slice(0, maxTagsToShow);
    
    // Filter items based on selected tags
    const filteredItems = React.useMemo(() => {
        if (selectedTags.length === 0) {
            return items;
        }
        
        return items.filter(item => {
            const itemTags = getItemTags(item);
            return itemTags && itemTags.some(tag =>
                selectedTags.some(selectedTag => selectedTag.title === tag.title)
            );
        });
    }, [items, selectedTags, getItemTags]);
    
    // Notify parent of filtered items changes
    React.useEffect(() => {
        onFilteredItemsChange(filteredItems);
    }, [filteredItems, onFilteredItemsChange]);
    
    const handleTagToggle = (tag: Tag) => {
        setSelectedTags(prev => {
            const isSelected = prev.some(t => t.title === tag.title);
            if (isSelected) {
                return prev.filter(t => t.title !== tag.title);
            } else {
                return [...prev, tag];
            }
        });
    };
    
    const clearFilters = () => {
        setSelectedTags([]);
    };
    
    if (allTags.length === 0) return null;
    
    return (
        <FlexCol gap={1} >
            <FlexRow gap={.5} align="center" style={{ flexWrap: "wrap" }}>
                <BCHeader4 className={'filter-preface-text'} text="Filter:" />
                {displayedTags.map((tag, index) => {
                    const isSelected = selectedTags.some(t => t.title === tag.title);
                    return (
                        <FlexRow
                            key={index}
                            borderRadius={0.5}
                            gap={0.5}
                            className={`tag-filter-button tag-${tag.color} ${isSelected ? 'selected' : ''}`}
                            pVertical={0.3}
                            pHorizontal={0.5}
                            justify="center"
                            align="center"
                            onClick={() => handleTagToggle(tag)}
                            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                        >
                            {tag.title}
                        </FlexRow>
                    );
                })}
                
                {allTags.length > maxTagsToShow && (
                    <BCTertiaryButton
                        className={'filter-cta-button'}
                        text={showAllTags ? "Show less" : `Show ${allTags.length - maxTagsToShow} more`}
                        onClick={() => setShowAllTags(!showAllTags)}
                        flexShrink={true}
                    />
                )}
                
                {selectedTags.length > 0 && (
                    <BCTertiaryButton
                        className={'filter-cta-button'}
                        text="Clear filters"
                        onClick={clearFilters}
                        flexShrink={true}
                    />
                )}
            </FlexRow>
            
            {selectedTags.length > 0 && (
                <Body2 text={`Showing ${filteredItems.length} of ${items.length} items`} />
            )}
        </FlexCol>
    );
}; 